/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map, tap, startWith } from 'rxjs/operators';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { v1 as uuid } from 'uuid';
import { RoutingService } from 'src/app/service/routing.service';
import { getScoreNameInfo } from 'src/app/shared/utils/score-type-details';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { UserSettingsApiService } from 'src/app/shared/services/customer-api/user-settings-api.service';
import { FilterListService } from 'src/app/cockpit/main-menu/menu-filters/service/filter-list.service';
import {
  ScoreScale,
  YukkaScore,
  YUKKA_SCORES,
  YUKKA_SCORES_BY_UID,
} from './scorelab-yukka-scores';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-scorelab-setup',
  templateUrl: './scorelab-setup.component.html',
  styleUrls: ['./scorelab-setup.component.scss'],
})
export class ScorelabSetupComponent implements OnInit {
  /** The built-in score on show, or undefined for a custom/draft score. */
  get currentYukkaScore(): YukkaScore | undefined {
    return this.currentScoreType === 'yukka'
      ? YUKKA_SCORES_BY_UID.get(this.currentScore?.uid)
      : undefined;
  }

  /**
   * Pendo hook for the set-default action, carrying which score it acts on.
   * Hyphenated to match the `option-<score>-score` row classes; custom scores
   * share one token because their uids are generated.
   */
  get setDefaultHook(): string {
    const uid = this.currentYukkaScore?.uid;
    return 'pendo-set-default-' + (uid ? uid.replace(/_/g, '-') : 'custom');
  }

  /** Range legend for the info pane; absent where the scale has no semantics. */
  get scoreScale(): ScoreScale | null {
    return this.currentYukkaScore?.scale ?? null;
  }

  @ViewChild('eventsContainer') eventsContainer: ElementRef;
  @ViewChildren('inputScoreEvent') scoreEventFields: QueryList<ElementRef>;

  isButtonActive = false;

  params: any;

  currentScore: any = {
    name: '',
    uid: '',
    event_weights: [],
    sentiment_type: '',
    sentiment_weight: null,
  };
  currentScoreType = '';
  /** Built-in scores this user may see, in display order. */
  visibleYukkaScores: YukkaScore[] = [];
  customScores = [];
  subEvents = {};
  subEventsIds = {};
  eventsList: any;

  notitle = false;

  eventsForms = [];
  eventsGroupsFiltered$: Observable<any>[] = [];
  eventsGroupsOptions = [];

  weightOptions = [1, 2, 3, 4, 5];
  polarityOptions = [
    { label: 'Positive', value: 1 },
    { label: 'Negative', value: -1 },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public routing: RoutingService,
    public featureFlagsService: FeatureFlagsService,
    private userSettingsApi: UserSettingsApiService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    public snackBar: MatSnackBar,
    private filterListService: FilterListService,
    private destroyRef: DestroyRef,
  ) {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.params = JSON.parse(JSON.stringify(params));
      });
    const settings = this.featureFlagsService.scorelabSettings ?? {};
    this.visibleYukkaScores = YUKKA_SCORES.filter(
      (score) => settings[score.access],
    );
  }

  ngOnInit() {
    this.filterListService.getEventList().subscribe((eventList) => {
      this.eventsList = JSON.parse(JSON.stringify(eventList));
      this.processEventList();
    });
  }

  private processEventList(): void {
    Object.entries(this.eventsList.subEvents).forEach(([, value]) => {
      // @ts-expect-error
      value.forEach((item) => {
        this.subEvents[item.value] = item.label;
        this.subEventsIds[item.label] = item.value;
      });
    });
    this.eventsGroupsOptions = this.eventsList.events.map((el) => {
      const events = this.eventsList.subEvents[el.value].map((item) => {
        return item.label;
      });
      return {
        label: el.label,
        events: events,
      };
    });

    this.initialize();
  }

  initialize() {
    this.notitle = false;
    if (this.featureFlagsService.scorelabSettings.customScores) {
      this.customScores = JSON.parse(
        JSON.stringify(this.featureFlagsService.scorelabSettings.customScores),
      );
    }
    const defaultScore = this.featureFlagsService.scorelabSettings.defaultScore;
    if (defaultScore) {
      this.selectScore(defaultScore);
    }
  }

  updateEventsControls() {
    this.eventsForms = [];
    this.eventsGroupsFiltered$ = [];
    const currentScoreCopy = JSON.parse(JSON.stringify(this.currentScore));
    currentScoreCopy.event_weights.forEach((eventObj) => {
      const eventsFormBuilder = this.formBuilder.group({
        eventsFormGroup: [
          this.subEvents[eventObj.event_id],
          Validators.compose([
            Validators.required,
            (c) => this.eventsNameValidator(c),
          ]),
        ],
      });
      this.eventsForms.push(eventsFormBuilder);
    });
    this.eventsGroupsFiltered$.length = currentScoreCopy.event_weights.length;
    this.eventsForms.forEach((eventsForm, index) => {
      this.eventsGroupsFiltered$[index] = this.eventsForms[index]
        .get('eventsFormGroup')!
        .valueChanges.pipe(
          tap((value) => {
            const property = '' + value;
            if (
              Object.prototype.hasOwnProperty.call(this.subEventsIds, property)
            ) {
              this.currentScore.event_weights[index].event_id =
                this.subEventsIds[property];
            } else {
              this.currentScore.event_weights[index].event_id = undefined;
            }
          }),
          startWith(
            this.subEvents[currentScoreCopy.event_weights[index].event_id],
          ),
          map((value) => this.groupMatches(value)),
        );
    });
  }

  /**
   * validate event name if corresponding id exists
   */
  eventsNameValidator(control) {
    return !Object.prototype.hasOwnProperty.call(
      this.subEventsIds,
      control.value,
    )
      ? { invalidEventName: true }
      : null;
  }

  /**
   * returns matching items
   */
  elementMatches(options, value) {
    const filterValue = value.toLowerCase();
    return options.filter(
      (item) => item.toLowerCase().indexOf(filterValue) === 0,
    );
  }

  /**
   * returns matching group's items
   */
  groupMatches(value) {
    if (value) {
      return this.eventsGroupsOptions
        .map((group) => ({
          label: group.label,
          events: this.elementMatches(group.events, value),
        }))
        .filter((group) => group.events.length > 0);
    }
    return this.eventsGroupsOptions;
  }

  /**
   * Every mutation here ends by re-running the data fetch. `score` is always
   * cleared, and a focused `time` is dropped along with it.
   */
  private refresh(queryParams: Record<string, unknown>): void {
    this.router.navigate([], {
      queryParams: {
        ...queryParams,
        score: null,
        time: this.params.score ? null : this.params.time,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Patches cockpit settings, persists them, then confirms via snack bar. */
  private persistCockpitSettings(
    patch: Record<string, unknown>,
    message: string,
    onSaved?: () => void,
  ): void {
    const settings = {
      ...this.featureFlagsService.cockpitSettings,
      ...patch,
    };
    this.featureFlagsService.cockpitSettings = settings;
    this.userSettingsApi.setCustomSettings(settings).subscribe(() => {
      this.snackBar.open(message, 'OK', { duration: 5000 });
      onSaved?.();
    });
  }

  /** Derives the fields the API expects before a custom score is persisted. */
  private stampEventWeights(): void {
    this.currentScore.sentiment_weight = 0;
    this.currentScore.sentiment_type = this.currentScore.event_weights.some(
      (event) => event.polarity === 1,
    )
      ? 'pos'
      : 'neg';
    this.currentScore.event_weights.forEach((event) => {
      event.weight_persistence = 90;
    });
  }

  /** Mirrors the local custom-score list into scorelab settings. */
  private syncCustomScores(): void {
    this.featureFlagsService.scorelabSettings = {
      ...this.featureFlagsService.scorelabSettings,
      customScores: JSON.parse(JSON.stringify(this.customScores)),
    };
  }

  selectScore(value) {
    this.notitle = false;
    const yukkaScore = YUKKA_SCORES_BY_UID.get(value);
    if (yukkaScore) {
      this.currentScore = {
        name: yukkaScore.label,
        uid: yukkaScore.uid,
        event_weights: [],
        sentiment_type: '',
        sentiment_weight: yukkaScore.sentimentWeight,
      };
      this.currentScoreType = 'yukka';
      return;
    }
    this.currentScore = this.customScores.find((score) => score.uid === value);
    this.currentScoreType = 'custom';
    this.updateEventsControls();
  }

  setAsDefault(newDefaultId) {
    this.selectScore(newDefaultId);
    this.featureFlagsService.scorelabSettings = {
      ...this.featureFlagsService.scorelabSettings,
      defaultScore: newDefaultId,
    };
    const newDefaultName = getScoreNameInfo(
      this.featureFlagsService.scorelabSettings,
    ).full;
    this.persistCockpitSettings(
      { user_defaultScore: newDefaultId },
      'Default score set to: ' + newDefaultName + '.',
    );
    // Sentiment sorts on its own key; any other score sorts on `score`. Without
    // an existing sort in the URL there is nothing to re-point.
    if (!this.params.sort) {
      this.refresh({ update: Math.random() });
    } else {
      this.refresh({
        update: Math.random(),
        sort: newDefaultId === 'sentiment' ? '-senti' : 'score',
      });
    }
  }

  createScore() {
    this.notitle = false;
    this.currentScoreType = 'new';
    this.currentScore = {
      name: '',
      uid: uuid(),
      event_weights: [
        {
          event_id: undefined,
          weight: 5,
          polarity: 1,
        },
      ],
    };
    this.updateEventsControls();
  }

  isValidScore(score) {
    let isValid = true;
    if (!score.name.trim()) {
      this.notitle = true;
      isValid = false;
    }
    if (!score.event_weights) {
      isValid = false;
    } else {
      const checkEl = (el) =>
        !el.event_id || el.event_id === undefined || !el.polarity || !el.weight;
      score.event_weights.forEach((el, index) => {
        if (checkEl(el)) {
          isValid = false;
          if (this.scoreEventFields) {
            this.scoreEventFields.toArray()[index].nativeElement.focus();
          }
        }
      });
    }
    return isValid;
  }

  saveScore() {
    const isNew = this.currentScoreType === 'new';
    if (!isNew && this.currentScoreType !== 'custom') {
      return;
    }
    this.notitle = false;
    if (!this.isValidScore(this.currentScore)) {
      alert('Error: all of the required fields must be valid.');
      return;
    }
    this.stampEventWeights();
    if (isNew) {
      this.customScores.push(this.currentScore);
    }
    this.syncCustomScores();
    this.persistCockpitSettings(
      { user_customScores: this.customScores },
      isNew ? 'Custom score created.' : 'Custom score saved.',
      isNew ? () => this.selectScore(this.currentScore.uid) : undefined,
    );
    if (isNew) {
      this.refresh({ updateScores: Math.random() });
    } else if (
      this.currentScore.uid ===
      this.featureFlagsService.scorelabSettings.defaultScore
    ) {
      this.refresh({ update: Math.random() });
    } else {
      this.refresh({ updateScoresOnSave: Math.random() });
    }
  }

  deleteScore() {
    if (
      this.featureFlagsService.scorelabSettings.defaultScore ===
      this.currentScore.uid
    ) {
      alert(
        'Please select another score as default before deleting tool`s current default one.',
      );
      return;
    }
    const deletedUid = this.currentScore.uid;
    this.customScores = this.customScores.filter(
      (score) => score.uid !== deletedUid,
    );
    this.syncCustomScores();
    this.persistCockpitSettings(
      { user_customScores: this.customScores },
      'Custom score removed.',
      () => this.dropScannerColumns(deletedUid),
    );
    this.initialize();
  }

  /** A deleted score must not linger as a scanner column or its delta. */
  private dropScannerColumns(deletedUid: string): void {
    this.userSettingsApi
      .getUserSettings('scannerColumns')
      .subscribe((result) => {
        if (!result.entry) {
          return;
        }
        const columns = JSON.parse(result.entry).filter(
          (item) => item !== deletedUid && item !== deletedUid + '_delta',
        );
        this.userSettingsApi
          .setUserSettings('scannerColumns', JSON.stringify(columns))
          .subscribe(() => this.refresh({ updateScores: Math.random() }));
      });
  }

  addEvent() {
    this.currentScore.event_weights.push({
      event_id: undefined,
      weight: 5,
      polarity: 1,
    });
    this.updateEventsControls();
    setTimeout(() => {
      this.scrollToBottom();
    }, 0);
  }

  removeEvent(index) {
    this.currentScore.event_weights.splice(index, 1);
    this.updateEventsControls();
  }

  scrollToBottom() {
    this.eventsContainer.nativeElement.scrollTop =
      this.eventsContainer.nativeElement.scrollHeight;
  }

  iClose() {
    this.dialog.closeAll();
  }
}
